import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/bda9954e-e13e-43a3-bde6-1da05ba07e44.png';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const currentLang = i18n.language;

  const toggleLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLangOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isLangOpen && !(e.target as Element).closest('.lang-selector')) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isLangOpen]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-navy-900 shadow-md' : 'bg-transparent'
    }`}>
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-36">

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 cursor-pointer">
            <div className="w-36 h-36 flex items-center justify-center">
              <img
                src={LOGO_URL}
                alt="Hopla French Riviera"
                className="w-36 h-36 object-contain"
              />
            </div>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#fleet" className={`text-sm font-medium transition-colors cursor-pointer ${
              isScrolled ? 'text-white hover:text-turquoise-300' : 'text-white hover:text-sand-200'
            }`}>
              {t('nav.ourFleet')}
            </a>
            <a href="#contact" className={`text-sm font-medium transition-colors cursor-pointer ${
              isScrolled ? 'text-white hover:text-turquoise-300' : 'text-white hover:text-sand-200'
            }`}>
              {t('nav.contact')}
            </a>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {/* Language selector */}
            <div className="relative lang-selector">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg transition-all cursor-pointer ${
                  isScrolled
                    ? 'bg-white/10 hover:bg-white/20 text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm font-medium uppercase">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isLangOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-sand-200 overflow-hidden min-w-[120px]">
                  <button
                    onClick={() => toggleLanguage('fr')}
                    className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors cursor-pointer flex items-center space-x-2 ${
                      currentLang === 'fr' ? 'bg-ocean-50 text-ocean-600' : 'text-navy-700 hover:bg-sand-50'
                    }`}
                  >
                    <span>🇫🇷</span>
                    <span>Français</span>
                    {currentLang === 'fr' && <i className="ri-check-line ml-auto text-ocean-500"></i>}
                  </button>
                  <button
                    onClick={() => toggleLanguage('en')}
                    className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors cursor-pointer flex items-center space-x-2 ${
                      currentLang === 'en' ? 'bg-ocean-50 text-ocean-600' : 'text-navy-700 hover:bg-sand-50'
                    }`}
                  >
                    <span>🇬🇧</span>
                    <span>English</span>
                    {currentLang === 'en' && <i className="ri-check-line ml-auto text-ocean-500"></i>}
                  </button>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <a
              href="#contact"
              className="px-6 py-2.5 bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              {t('nav.bookNow')}
            </a>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
                isScrolled ? 'text-white' : 'text-white'
              }`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white rounded-xl shadow-xl mt-2 p-4 border border-sand-200">
            <div className="flex flex-col space-y-4">
              <a href="#fleet" onClick={() => setIsMobileMenuOpen(false)} className="text-navy-700 font-medium hover:text-ocean-500 cursor-pointer">{t('nav.ourFleet')}</a>
              <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="text-navy-700 font-medium hover:text-ocean-500 cursor-pointer">{t('nav.contact')}</a>
              <div className="flex items-center space-x-2 pt-2 border-t border-sand-200">
                <button
                  onClick={() => { toggleLanguage('fr'); setIsMobileMenuOpen(false); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium cursor-pointer ${currentLang === 'fr' ? 'bg-ocean-100 text-ocean-600' : 'bg-sand-100 text-navy-600'}`}
                >
                  🇫🇷 FR
                </button>
                <button
                  onClick={() => { toggleLanguage('en'); setIsMobileMenuOpen(false); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium cursor-pointer ${currentLang === 'en' ? 'bg-ocean-100 text-ocean-600' : 'bg-sand-100 text-navy-600'}`}
                >
                  🇬🇧 EN
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
